/**
 * ProjectionPage — Fullscreen projection window for the visualizer.
 *
 * Opened via window.open() from the sidebar. Renders the active visualization
 * at full resolution without any UI chrome.
 *
 * This does NOT use VisualizationEngine (which creates its own BroadcastChannel
 * and would conflict). Instead it directly:
 * - Listens on BroadcastChannel for state + frame messages from the sidebar engine
 * - Instantiates the active Visualization and renders each frame
 * - Handles brightness, blackout, frozen states locally
 *
 * Close via: hover controls (top edge), or sidebar close button.
 */

import { useEffect, useRef, useState } from 'react';
import { X, Maximize } from 'lucide-react';
import { createVisualizationById } from './visualizations';
import { FrameData, Visualization, VisualizerState } from './types';

export default function ProjectionPage() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const vizRef = useRef<Visualization | null>(null);
  const stateRef = useRef<VisualizerState | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const [connected, setConnected] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [cursorVisible, setCursorVisible] = useState(true);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cursorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestFrameRef = useRef<Partial<FrameData> | null>(null);

  // Silent frame data for rendering when no audio data arrives
  const silentFreqData = useRef(new Uint8Array(1024));
  const silentTimeData = useRef(new Uint8Array(1024).fill(128));

  // Main effect — runs once on mount, never re-runs
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Size canvas to full window. Guards against 0-dimension transitions
    // that happen on Windows during fullscreen/maximize animations.
    const resize = () => {
      const w = window.innerWidth || screen.width;
      const h = window.innerHeight || screen.height;
      if (w === 0 || h === 0) return;  // skip transient 0-dimension states
      if (canvas.width === w && canvas.height === h) return;  // no-op if unchanged
      canvas.width = w;
      canvas.height = h;
      vizRef.current?.resize(w, h);
    };
    resize();
    window.addEventListener('resize', resize);

    // Track fullscreen state — also re-trigger resize since the 'resize' event
    // may not fire (or fires with stale dimensions) during fullscreen transitions.
    const onFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      // Immediate resize for browsers that update dimensions synchronously
      resize();
      // Delayed resize to catch browsers (especially on Windows) where the
      // fullscreen geometry isn't final until after the event fires
      setTimeout(resize, 50);
      setTimeout(resize, 150);
      setTimeout(resize, 300);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);

    // Handle WebGL context loss — some GPUs lose the context during resize.
    // On restore, re-initialize the active visualization.
    const handleContextLost = (e: Event) => {
      e.preventDefault(); // allow context restore
    };
    const handleContextRestored = () => {
      const viz = vizRef.current;
      const state = stateRef.current;
      if (viz && state?.activeVisualizationId) {
        // Re-create the visualization from scratch since all GL state is lost
        viz.destroy();
        const newViz = createVisualizationById(state.activeVisualizationId);
        if (newViz) {
          vizRef.current = newViz;
          newViz.init(canvas);
          newViz.resize(canvas.width, canvas.height);
        }
      }
    };
    canvas.addEventListener('webglcontextlost', handleContextLost);
    canvas.addEventListener('webglcontextrestored', handleContextRestored);

    // Cursor auto-hide on idle
    const handleMouseMove = (e: MouseEvent) => {
      setCursorVisible(true);
      if (cursorTimerRef.current) clearTimeout(cursorTimerRef.current);
      cursorTimerRef.current = setTimeout(() => setCursorVisible(false), 3000);

      // Show control bar when mouse is near top edge
      if (e.clientY < 60) {
        setShowControls(true);
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        hideTimerRef.current = setTimeout(() => setShowControls(false), 2500);
      }
    };
    window.addEventListener('mousemove', handleMouseMove);

    // Start cursor hide timer
    cursorTimerRef.current = setTimeout(() => setCursorVisible(false), 3000);

    // Set up BroadcastChannel to receive from the sidebar engine
    let channel: BroadcastChannel | null = null;
    try {
      channel = new BroadcastChannel('votebeats-visualizer');
    } catch {
      // BroadcastChannel not available — nothing will work
    }

    if (channel) {
      channel.onmessage = (event) => {
        const msg = event.data;
        switch (msg.type) {
          case 'state': {
            setConnected(true);
            const newState = msg.state as VisualizerState;
            const prevVizId = stateRef.current?.activeVisualizationId;
            stateRef.current = newState;

            // Switch visualization if changed (or first time)
            if (newState.activeVisualizationId && prevVizId !== newState.activeVisualizationId) {
              const newViz = createVisualizationById(newState.activeVisualizationId);
              if (newViz) {
                vizRef.current?.destroy();
                vizRef.current = newViz;
                newViz.init(canvas);
                newViz.resize(canvas.width, canvas.height);
              }
            }
            break;
          }
          case 'frame': {
            latestFrameRef.current = {
              frequencyData: msg.frequencyData ? new Uint8Array(msg.frequencyData) : undefined,
              timeDomainData: msg.timeDomainData ? new Uint8Array(msg.timeDomainData) : undefined,
              bassEnergy: msg.bassEnergy,
              midEnergy: msg.midEnergy,
              trebleEnergy: msg.trebleEnergy,
              overallEnergy: msg.overallEnergy,
              isBeat: msg.isBeat,
              beatIntensity: msg.beatIntensity,
              bpm: msg.bpm,
            };
            break;
          }
          case 'audio-disconnected':
            setConnected(false);
            latestFrameRef.current = null;
            break;
        }
      };

      // Request initial state — retry a few times in case of timing
      channel.postMessage({ type: 'request-state' });
      setTimeout(() => { if (!stateRef.current) channel?.postMessage({ type: 'request-state' }); }, 500);
      setTimeout(() => { if (!stateRef.current) channel?.postMessage({ type: 'request-state' }); }, 1500);
      setTimeout(() => { if (!stateRef.current) channel?.postMessage({ type: 'request-state' }); }, 3000);
    }

    // Render loop
    const renderLoop = () => {
      animFrameRef.current = requestAnimationFrame(renderLoop);
      const state = stateRef.current;
      const viz = vizRef.current;
      if (!state || !viz) return;
      if (state.frozen) return;

      const now = performance.now();
      const latest = latestFrameRef.current;

      const frame: FrameData = {
        frequencyData: (latest?.frequencyData as Uint8Array) || silentFreqData.current,
        timeDomainData: (latest?.timeDomainData as Uint8Array) || silentTimeData.current,
        bassEnergy: latest?.bassEnergy ?? 0,
        midEnergy: latest?.midEnergy ?? 0,
        trebleEnergy: latest?.trebleEnergy ?? 0,
        overallEnergy: latest?.overallEnergy ?? 0,
        isBeat: latest?.isBeat ?? false,
        beatIntensity: latest?.beatIntensity ?? 0,
        bpm: latest?.bpm ?? 0,
        timestamp: now,
        deltaTime: 16.67,
        elapsed: now,
      };

      if (state.blackout) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = '#000';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        return;
      }

      viz.render(frame);

      if (state.brightness < 1) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.fillStyle = `rgba(0, 0, 0, ${1 - state.brightness})`;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
      }
    };
    animFrameRef.current = requestAnimationFrame(renderLoop);

    // Fullscreen toggle on double-click
    const handleDblClick = () => {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        document.documentElement.requestFullscreen();
      }
    };
    canvas.addEventListener('dblclick', handleDblClick);

    // F11 for fullscreen
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'F11') {
        e.preventDefault();
        handleDblClick();
      }
    };
    window.addEventListener('keydown', handleKey);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      canvas.removeEventListener('dblclick', handleDblClick);
      canvas.removeEventListener('webglcontextlost', handleContextLost);
      canvas.removeEventListener('webglcontextrestored', handleContextRestored);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (cursorTimerRef.current) clearTimeout(cursorTimerRef.current);
      vizRef.current?.destroy();
      channel?.close();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goFullscreen = () => {
    document.documentElement.requestFullscreen().catch(() => {});
  };

  const exitFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
    }
  };

  return (
    <div
      className="w-screen h-screen bg-black overflow-hidden"
      style={{ cursor: cursorVisible ? 'default' : 'none' }}
    >
      <canvas
        ref={canvasRef}
        className="w-full h-full block"
      />

      {/* Fullscreen prompt — shown until user goes fullscreen */}
      {!isFullscreen && connected && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50">
          <button
            onClick={goFullscreen}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-purple-600/90 hover:bg-purple-600 text-white text-sm font-medium backdrop-blur-sm transition-colors shadow-lg animate-pulse"
            style={{ cursor: 'pointer' }}
          >
            <Maximize className="w-4 h-4" />
            Go Fullscreen
          </button>
        </div>
      )}

      {/* Hover controls bar — appears when mouse moves to top edge */}
      <div
        className={`fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4 py-2 bg-black/70 backdrop-blur-sm transition-all duration-300 ${
          showControls ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'
        }`}
        style={{ cursor: 'default' }}
      >
        <span className="text-xs text-gray-400 font-mono">
          VoteBeats Projection
        </span>
        <div className="flex items-center gap-2">
          {isFullscreen && (
            <button
              onClick={exitFullscreen}
              className="px-3 py-1 rounded text-xs text-gray-300 hover:text-white bg-gray-700/60 hover:bg-gray-700 transition-colors"
              style={{ cursor: 'pointer' }}
            >
              Exit Fullscreen
            </button>
          )}
          <button
            onClick={() => window.close()}
            className="p-1.5 rounded-full text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
            title="Close projection window"
            style={{ cursor: 'pointer' }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Connection indicator */}
      {!connected && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
          <div className="text-sm text-gray-500 font-mono">Waiting for VoteBeats...</div>
          <div className="text-xs text-gray-600">Start the visualizer in the sidebar, then open this window</div>
        </div>
      )}
    </div>
  );
}
